import React, { useState, useEffect, useMemo } from 'react'
import { vendorWellService } from '../supabase'
import './ConstructionRecordList.css'

// 定義要在表格中顯示的欄位 (這裡以最常用/重要的欄位為主，可依需求增減)
const columns = [
    { key: 'repair_date', label: '修復日期' },
    { key: 'e_num', label: '電號' },
    { key: 'name', label: '水井名稱' },
    { key: 'unit', label: '站別' },
    { key: 'hp', label: '馬力' },
    { key: 'motor_type', label: '馬達類型' },
    { key: 'motor_vendor', label: '抽水機廠牌' },
    { key: 'pump_depth', label: '安裝深度(m)' },
    { key: 'outlet_pipe_diameter(inch)', label: '出水管徑' },
    { key: 'outlet_pipe_material', label: '出水管材質' },
    { key: 'pump_depth', label: 'pump安裝深度' },
    { key: 'vendor', label: '施工廠商' },
]

const ConstructionRecordList = ({ isOpen, onClose }) => {
    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [sortConfig, setSortConfig] = useState({ key: 'repair_date', direction: 'desc' })

    // 載入施工紀錄
    useEffect(() => {
        const fetchRecords = async () => {
            if (!isOpen) return

            try {
                setLoading(true)
                const data = await vendorWellService.getAllConstructionRecords()
                setRecords(data || [])
            } catch (err) {
                console.error('載入施工紀錄失敗:', err)
                setError('無法載入施工紀錄，請稍後再試。')
            } finally {
                setLoading(false)
            }
        }

        fetchRecords()
    }, [isOpen])

    // 處理排序邏輯
    const handleSort = (key) => {
        let direction = 'asc'
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc'
        }
        setSortConfig({ key, direction })
    }

    // 取得排序圖示
    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return '↕' // 預設雙向箭頭
        }
        return sortConfig.direction === 'asc' ? '↑' : '↓'
    }

    // 過濾與排序資料
    const processedRecords = useMemo(() => {
        let filteredData = [...records]

        // 1. 搜尋過濾 (比對所有顯示欄位)
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase()
            filteredData = filteredData.filter(record => {
                return columns.some(col =>
                    String(record[col.key] || '').toLowerCase().includes(lowerSearch)
                )
            })
        }

        // 2. 排序
        if (sortConfig.key) {
            filteredData.sort((a, b) => {
                const valueA = a[sortConfig.key] === null || a[sortConfig.key] === undefined ? '' : String(a[sortConfig.key])
                const valueB = b[sortConfig.key] === null || b[sortConfig.key] === undefined ? '' : String(b[sortConfig.key])

                // 嘗試轉為數字比較 (針對馬力、深度等)，若無法轉換則以字串比較
                const numA = parseFloat(valueA)
                const numB = parseFloat(valueB)

                if (!isNaN(numA) && !isNaN(numB)) {
                    return sortConfig.direction === 'asc' ? numA - numB : numB - numA
                }

                // 字串比較 (Locale 確保中文排序也夠準確)
                if (valueA < valueB) {
                    return sortConfig.direction === 'asc' ? -1 : 1
                }
                if (valueA > valueB) {
                    return sortConfig.direction === 'asc' ? 1 : -1
                }
                return 0
            })
        }

        return filteredData
    }, [records, searchTerm, sortConfig, columns])

    if (!isOpen) return null

    return (
        <div className="modal-overlay">
            <div className="modal-content large">
                <div className="modal-header">
                    <h2>施工紀錄列表 (共 {processedRecords.length} 筆)</h2>
                    <button className="close-btn" onClick={onClose} title="關閉">×</button>
                </div>

                <div className="record-list-container">
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="搜尋名稱、廠商、日期或原因..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {loading ? (
                        <div className="loading-state">載入中，請稍候...</div>
                    ) : error ? (
                        <div className="error-state">{error}</div>
                    ) : processedRecords.length === 0 ? (
                        <div className="no-data-state">目前的條件下沒有找到任何施工紀錄。</div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="excel-table">
                                <thead>
                                    <tr>
                                        {columns.map((col) => (
                                            <th
                                                key={col.key}
                                                onClick={() => handleSort(col.key)}
                                                title={`點擊以 ${sortConfig.key === col.key && sortConfig.direction === 'asc' ? '遞減' : '遞增'} 排序`}
                                            >
                                                {col.label}
                                                <span className={`sort-icon ${sortConfig.key === col.key ? 'active' : ''}`}>
                                                    {getSortIcon(col.key)}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedRecords.map((record, index) => (
                                        <tr key={record.id || index}>
                                            {columns.map(col => (
                                                <td key={`${record.id}-${col.key}`}>
                                                    {record[col.key] || '-'}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ConstructionRecordList
